<script>
import Tooltip from '@components/tooltip.vue'
import MottoIcon from '@components/motto-icon.vue'
import CopyToClipboardButton from '@components/copy-to-clipboard-button.vue'
import { referralStatusTooltips } from '@utils/consts/referralStatusTooltips'
import moment from 'moment'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import { authComputed } from '@state/helpers'

export default {
  components: {
    Tooltip,
    MottoIcon,
    CopyToClipboardButton,
  },
  mixins: [isMobileMixin],
  props: {
    tableData: {
      type: Array,
      default: () => [],
    },
    tableFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      moment: moment,
    }
  },
  computed: {
    ...authComputed,
    primaryOfficeTradeName() {
      return this.currentUser.primaryOffice?.tradeName || ''
    },
  },
  methods: {
    getTrClass(item) {
      const classes = [this.$style.rosterItemRow]
      const pendingStatuses = ['Open', 'New', 'Pending Confirmation', 'Pending Assignment', 'In Progress']
      const referralStatus = item.officeReceivedStatus || item.receivedStatus || item.status

      if (!item.active) {
        return classes
      }

      if (pendingStatuses.includes(referralStatus)) {
        return [...classes, this.$style.rosterItemRowOrange]
      }

      return [...classes, this.$style.rosterItemRowGreen]
    },

    goToReferral({ id, referralCategory }) {
      let routeName =
        referralCategory === 'sent' || referralCategory === 'office_sent' ? 'sent-referral' : 'received-referral'
      if (referralCategory === 'office_received') {
        routeName = 'received-office-referral'
      }
      this.$router.push({
        name: routeName,
        params: {
          id,
        },
      })
    },
    getStatusTooltipText(status, referral) {
      if (
        (referral.referralCategory === 'received' || referral.referralCategory === 'office_received') &&
        status === 'Completed'
      ) {
        status = 'CompletedReceived'
      }
      return referralStatusTooltips({
        status,
        tradeName: this.primaryOfficeTradeName,
      })
    },
    getAcceptedUserForCompletedReferral(referral) {
      if (referral.completedUser) {
        return referral.completedUser
      }

      return referral.users ? referral.users[0] : {}
    },
  },
}
</script>

<template>
  <b-table
    v-if="tableData.length"
    ref="rosterTable"
    :class="$style.rosterTable"
    :fields="tableFields"
    :items="tableData"
    :tbody-tr-class="getTrClass"
    borderless
    no-border-collapse
    responsive
    sort-icon-left
    @row-clicked="goToReferral">
    <template v-slot:cell(state)="data">
      <template v-if="isMobile">
        <div
          v-if="data.item.referralCategory === 'received' || data.item.referralCategory === 'office_received'"
          :class="$style.mobileReferralItem">
          <div class="d-flex justify-content-between">
            <p>
              {{ data.item.state }}
            </p>
            <p>
              {{ data.item.officeReceivedStatus || data.item.receivedStatus }}
            </p>
          </div>
          <p>Loan Type: {{ data.item.loanType || 'N/A' }}</p>
          <p>Loan Amount: {{ data.item.loanAmount || 'N/A' }}</p>
          <p>Date Received: {{ moment.unix(data.item.receivedAt).format('M/D/yyyy') }}</p>
          <p>Sender: {{ data.item.receivedStatus === 'Completed' ? data.item.sender : 'N/A' }}</p>
          <p>Assigned: {{ data.item.assigned || 'N/A' }}</p>
          <div class="d-flex align-items-center justify-content-end">
            <a
              v-if="data.item.receivedStatus === 'Completed'"
              :href="`tel:${data.item.createdBy.phone}`"
              :class="$style.contactIcon">
              <MottoIcon name="Phone" icon-color="orange" class="ml-3 mr-2"></MottoIcon>
            </a>
            <a
              v-if="data.item.receivedStatus === 'Completed'"
              :href="`mailto:${data.item.createdBy.email}?subject=Completed ${data.item.state || ''} ${
                data.item.loanType || ''
              } deal level referral`"
              :class="$style.contactIcon">
              <MottoIcon name="Envelope" icon-color="orange" class="ml-1 mr-3"></MottoIcon>
            </a>
          </div>
        </div>
        <div v-else :class="$style.mobileReferralItem">
          <div class="d-flex justify-content-between">
            <p>
              {{ data.item.state }}
            </p>
            <p>
              {{ data.item.status }}
            </p>
          </div>
          <p>Loan Type: {{ data.item.loanType || 'N/A' }}</p>
          <p>Loan Amount: {{ data.item.loanAmount || 'N/A' }}</p>
          <p>Recipients: {{ data.item.recipients }}</p>
          <p>Date Sent: {{ moment.unix(data.item.dateSent).format('M/D/yyyy') }}</p>
          <div class="d-flex align-items-center justify-content-end">
            <a
              v-if="data.item.status === 'Completed' && getAcceptedUserForCompletedReferral(data.item).phone"
              :href="`tel:${getAcceptedUserForCompletedReferral(data.item).phone || ''}`"
              :class="$style.contactIcon">
              <MottoIcon name="Phone" icon-color="orange" class="ml-3 mr-2"></MottoIcon>
            </a>
            <a
              v-if="data.item.status === 'Completed' && getAcceptedUserForCompletedReferral(data.item).email"
              :href="`mailto:${getAcceptedUserForCompletedReferral(data.item).email}?subject=Completed ${
                data.item.state || ''
              } ${data.item.loanType || ''} deal level referral`"
              :class="$style.contactIcon">
              <MottoIcon name="Envelope" icon-color="orange" class="ml-1 mr-3" />
            </a>
          </div>
        </div>
      </template>
      <template v-else>
        {{ data.value }}
      </template>
    </template>
    <template v-slot:cell(sender)="data">
      {{ data.item.receivedStatus === 'Completed' ? data.value : 'N/A' }}
    </template>
    <template v-slot:cell(assigned)="data">
      {{ data.value || 'N/A' }}
    </template>
    <template v-slot:cell(recipients)="data">
      {{ data.value || 'N/A' }}
    </template>
    <template v-slot:cell(loanType)="data">
      {{ data.value || 'N/A' }}
    </template>
    <template v-slot:cell(loanAmount)="data">
      {{ data.value || 'N/A' }}
    </template>
    <template v-slot:cell(dateSent)="data">
      {{ moment.unix(data.value).format('M/D/yyyy') }}
    </template>
    <template v-slot:cell(dateReceived)="data">
      {{ moment.unix(data.value).format('M/D/yyyy') }}
    </template>
    <template v-slot:cell(status)="data">
      <div class="d-flex position-relative">
        <span class="d-flex align-items-center mr-4">
          {{ data.value }}
          <a
            v-if="data.value === 'Completed' && getAcceptedUserForCompletedReferral(data.item).phone"
            :class="$style.contactIcon">
            <MottoIcon :id="`phone${data.item.id}`" name="Phone" icon-color="orange" class="ml-3 mr-2" />
            <b-tooltip
              :triggers="isMobile ? '' : 'click blur'"
              :target="`phone${data.item.id}`"
              :placement="`top`"
              :custom-class="`tooltipBlue ${$style.copyToClipboardTooltip}`">
              {{ getAcceptedUserForCompletedReferral(data.item).phone }}
              <CopyToClipboardButton
                :text-to-copy="getAcceptedUserForCompletedReferral(data.item).phone"
                theme="square"
                icon="Copy" />
            </b-tooltip>
          </a>
          <a
            v-if="data.value === 'Completed' && getAcceptedUserForCompletedReferral(data.item).email"
            :href="`mailto:${getAcceptedUserForCompletedReferral(data.item).email}?subject=Completed ${
              data.item.state || ''
            } ${data.item.loanType || ''} deal level referral`"
            :class="$style.contactIcon">
            <MottoIcon
              :id="`envelope${data.item.id}`"
              name="Envelope"
              icon-color="orange"
              class="ml-1 mr-3"></MottoIcon>
            <b-tooltip :target="`envelope${data.item.id}`" :placement="`top`" custom-class="tooltipBlue">
              Email referree
            </b-tooltip>
          </a>
        </span>
        <Tooltip
          v-if="getStatusTooltipText(data.value, data.item)"
          :target="`${data.item.id}`"
          placement="left"
          theme="blue"
          :custom-class="$style.tableTooltipInner"
          :class="$style.tableTooltip">
          {{ getStatusTooltipText(data.value, data.item) }}
        </Tooltip>
      </div>
    </template>
    <template v-slot:cell(receivedStatus)="data">
      <div class="d-flex position-relative">
        <span class="d-flex align-items-center mr-4">
          {{ data.item.officeReceivedStatus || data.value }}
          <a v-if="data.value === 'Completed'" :class="$style.contactIcon">
            <MottoIcon :id="`phone${data.item.id}`" name="Phone" icon-color="orange" class="ml-3 mr-2" />
            <b-tooltip
              :triggers="isMobile ? '' : 'click blur'"
              :target="`phone${data.item.id}`"
              :placement="`top`"
              :custom-class="`tooltipBlue ${$style.copyToClipboardTooltip}`">
              {{ data.item.createdBy.phone }}
              <CopyToClipboardButton :text-to-copy="data.item.createdBy.phone" theme="square" icon="Copy" />
            </b-tooltip>
          </a>
          <a
            v-if="data.value === 'Completed'"
            :href="`mailto:${data.item.createdBy.email}?subject=Completed ${data.item.state || ''} ${
              data.item.loanType || ''
            } deal level referral`"
            :class="$style.contactIcon">
            <MottoIcon
              :id="`envelope${data.item.id}`"
              name="Envelope"
              icon-color="orange"
              class="ml-1 mr-3"></MottoIcon>
            <b-tooltip :target="`envelope${data.item.id}`" :placement="`top`" custom-class="tooltipBlue">
              Email referrer
            </b-tooltip>
          </a>
        </span>
        <Tooltip
          v-if="getStatusTooltipText(data.item.officeReceivedStatus || data.value, data.item)"
          :target="`${data.item.id}`"
          placement="left"
          theme="blue"
          :custom-class="$style.tableTooltipInner"
          :class="$style.tableTooltip">
          {{ getStatusTooltipText(data.item.officeReceivedStatus || data.value, data.item) }}
        </Tooltip>
      </div>
    </template>
  </b-table>
</template>

<style lang="scss">
.tooltipBlue {
  &.bs-tooltip-bottom {
    .tooltip-inner {
      border-radius: 8px 2px 8px 8px;
    }
  }

  &.bs-tooltip-top {
    .tooltip-inner {
      border-radius: 8px 8px 2px 8px;
    }
  }
}
</style>

<style lang="scss" module>
@import '@design';

.rosterTable table {
  @media (max-width: $lg-breakpoint) {
    border-spacing: 0;

    thead {
      display: none;
    }
  }

  border-spacing: 0 8px !important;

  /* ----- BOOTSTRAP TABLE SORT ICON STYLE ----- */

  thead > tr > th[aria-sort='none'] {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='101' height='101' view-box='0 0 101 101' preserveAspectRatio='none'%3e%3cpath fill='black' opacity='.3' d='M51 1l25 23 24 22H1l25-22z'/%3e%3cpath fill='black' opacity='.3' d='M51 101l25-23 24-22H1l25 22z'/%3e%3c/svg%3e") !important;
  }

  thead > tr > th[aria-sort='ascending'] {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='101' height='101' view-box='0 0 101 101' preserveAspectRatio='none'%3e%3cpath fill='%23003057' d='M51 1l25 23 24 22H1l25-22z'/%3e%3cpath fill='black' opacity='.3' d='M51 101l25-23 24-22H1l25 22z'/%3e%3c/svg%3e") !important;
  }

  thead > tr > [aria-sort='descending'] {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='101' height='101' view-box='0 0 101 101' preserveAspectRatio='none'%3e%3cpath fill='black' opacity='.3' d='M51 1l25 23 24 22H1l25-22z'/%3e%3cpath fill='%23003057' d='M51 101l25-23 24-22H1l25 22z'/%3e%3c/svg%3e") !important;
  }

  th {
    &:first-child {
      padding-left: 32px !important;
    }

    font-size: 14px;
    font-weight: 700;
    color: $gray;
  }
}

.rosterItemName {
  @media (max-width: $lg-breakpoint) {
    margin: 0 0 5px 0;
    word-break: break-word;
  }

  margin: 0;
  color: $dark-gray;
}

.rosterItemRowRed {
  td {
    border-color: $secondary-red !important;

    &::before {
      border-color: $secondary-red !important;
    }

    &::after {
      border-color: $secondary-red !important;
    }
  }
}

.rosterItemRowOrange {
  td {
    border-color: $secondary-orange !important;

    &::before {
      border-color: $secondary-orange !important;
    }

    &::after {
      border-color: $secondary-orange !important;
    }
  }
}

.rosterItemRowGreen {
  td {
    border-color: $secondary-green !important;

    &::before {
      border-color: $secondary-green !important;
    }

    &::after {
      border-color: $secondary-green !important;
    }
  }
}

.rosterItemRow {
  cursor: pointer;

  &:hover {
    background: $lightest-gray;
  }

  td {
    &:first-child {
      padding-left: 32px !important;
      border-style: solid none solid solid !important;
      border-top-left-radius: 10px !important;
      border-bottom-left-radius: 10px !important;
    }

    &:last-child {
      @media (max-width: $lg-breakpoint) {
        border-style: solid solid solid solid !important;
      }

      border-style: solid solid solid none !important;
      border-top-right-radius: 10px !important;
      border-bottom-right-radius: 10px !important;
    }

    vertical-align: middle !important;
    border-color: $light-gray;
    border-style: solid none solid none !important;
    border-width: 1px !important;
  }

  &[class*='b-table-has-details'] {
    background: $lightest-gray;

    td {
      border-bottom: none !important;
      border-bottom-right-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
    }
  }

  &[class*='b-table-details'] {
    position: relative;
    cursor: unset;

    &:hover {
      background: unset;
    }

    td {
      border-style: solid solid solid solid !important;
      border-top-left-radius: 0 !important;
      border-top-right-radius: 0 !important;

      &::before {
        position: absolute;
        top: -8px;
        left: -1px;
        height: 8px;
        content: '';
        border-style: solid !important;
        border-width: 1px !important;
      }

      &::after {
        position: absolute;
        top: -8px;
        right: 0;
        height: 8px;
        content: '';
        border-style: none solid none none !important;
        border-width: 1px !important;
      }
    }
  }
}

.mobileRosterItem {
  display: flex;
  justify-content: space-between;

  > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
}

.contactIcon {
  @media (max-width: $lg-breakpoint) {
    svg {
      width: 30px;
    }
  }

  color: unset;
}

.tableTooltip {
  > div {
    top: 50%;
    right: -2px;
    transform: translate(0, -50%);
  }
}

.tableTooltipInner {
  top: 2px !important;
  left: 10px !important;

  > div:first-child {
    &::after {
      position: absolute;
      right: 6px;
      bottom: 1px;
      display: block;
      width: 6px;
      height: 6px;
      content: '';
      background: $primary-blue;
    }
  }
}

.copyToClipboardTooltip {
  div[class='tooltip-inner'] {
    display: flex;
    align-items: center;
    gap: 0 15px;
  }
}
</style>
